/* Card Grid Item Component Styles */

.cardGridItem {
  position: relative; /* Positioning context for absolute children */
  background-color: #2d2d2d;
  border: 2px solid #3d3d3d;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.2s;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  height: fit-content;
  outline: none; /* Remove focus outline for cleaner look */
  will-change: transform, opacity;
  /* Suppress the browser's native long-press on the card (iOS image callout /
     "Save Image" menu, text selection, image drag) so our own long-press =
     preview gesture (handlePointerDown in CardGridItemReact) owns the hold. */
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
}

.cardGridItem:hover {
  border-color: #3b82f6;
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
}

.cardGridItem:focus {
  /* No visible focus indicator since hover already provides visual feedback */
  outline: none;
}

.cardGridItem.selected {
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px #3b82f6, 0 0 18px rgba(59, 130, 246, 0.55);
}

.cardGridItem.selected:hover {
  border-color: #3b82f6;
}

.selectCheck {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 2;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background-color: #3b82f6;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  box-shadow: 0 0 10px rgba(59, 130, 246, 0.7);
}

.cardGridItemImage {
  /* Bound to the card box below, which has to reproduce this exact height to
     size itself. Two places, one number. */
  --pile-card-height: 250px;
  width: 100%;
  height: var(--pile-card-height);
  background-color: #1a1a1a;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
}

/* Phone (design 1a): the desktop cells are wide enough (>=180px) that a card
   nearly fills the fixed 250px box, but the narrow 3-up phone cells (~112px)
   leave tall dead bands above/below the contained card. Size the image box to
   the card's own aspect ratio (63:88 = CARD_WIDTH:CARD_HEIGHT) so it hugs the
   art and more cards fit per screen. Breakpoint tracks docs/architecture/responsive.md. */
@media (max-width: 639px) {
  .cardGridItemImage {
    height: auto;
    aspect-ratio: 63 / 88;
  }
}

.cardImageContainer {
  position: relative;
  /**
   * The CARD, not the cell that holds it.
   *
   * The cell is a grid column wide (>=176px) and a fixed 250px tall, which is
   * not the shape of a Magic card. `object-fit: contain` on the images below
   * therefore centres the art and leaves transparent gutters either side — and
   * anything drawn at `inset: 0` on this box, the foil overlay above all, paints
   * across those gutters too. The rainbow shimmer ended up a wide rectangle with
   * the card floating inside it instead of a treatment hugging the card's edges.
   *
   * Giving this box the card's own 63:88 ratio makes the two coincide: the box
   * becomes the largest card-shaped rectangle that fits the cell, which is
   * precisely what `contain` was already drawing the art into. The images keep
   * `object-fit: contain` and are unaffected, because there is no longer
   * anything to letterbox.
   */
  aspect-ratio: 63 / 88;
  /**
   * Height-bound in any column with room for a 250px-tall card (178.9px of
   * width), width-bound in the narrowest ones — a 180px grid column less the
   * card's 2px borders leaves 176px, just under. `min()` picks the binding axis
   * and `aspect-ratio` derives the other, so the ratio survives either way.
   * Deriving the height rather than fixing it is the whole trick: `height: 100%`
   * with a clamped width would go back to being the wrong shape.
   */
  width: min(100%, calc(var(--pile-card-height) * 63 / 88));
  margin: auto;
}

@media (max-width: 639px) {
  /* Separate from the block above on purpose: this has to come *after* the base
     rule to win on source order, and that block sits before it.

     Here the cell already carries the card's ratio, so the card box is simply
     the whole cell — and `--pile-card-height` no longer describes anything,
     since the cell's height is derived rather than fixed. */
  .cardImageContainer {
    width: 100%;
  }
}

.cardGridItemImg {
  width: 100%;
  height: 100%;
  object-fit: contain; /* Changed from cover to contain to show full height */
  display: block;
  opacity: 0;
  transition: opacity 0.4s ease;
  position: absolute;
  top: 0;
  left: 0;
  /* The card div owns the touch gesture; the image must not intercept the hold
     as a native drag / callout target. */
  pointer-events: none;
  -webkit-user-drag: none;
  -webkit-touch-callout: none;
}

.cardGridItemImg.loaded {
  opacity: 1;
}

.cardGridItemImg.hidden {
  opacity: 0 !important;
}

.cardGridItemFallback {
  font-size: 32px;
  font-weight: bold;
  color: #6b7280;
}

/* Overlaid on the bottom of the card art (left-aligned), rather than a bar
   below it — keeps the cell card-shaped. A gradient scrim keeps the name legible
   over the art; the text clamps to 3 lines. */
.cardGridItemName {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 14px 10px 8px;
  font-size: 13px;
  font-weight: 600;
  color: #ffffff;
  text-align: left;
  line-height: 1.25;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.92) 35%, rgba(0, 0, 0, 0));
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  overflow: hidden;
  pointer-events: none;
}

.cardGridItemPosition {
  position: absolute;
  top: 8px;
  left: 8px;
  background-color: rgba(0, 0, 0, 0.8);
  color: #ffffff;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  backdrop-filter: blur(4px);
}

/* Skeleton loading state */
.skeleton {
  pointer-events: none;
  cursor: default;
}

.skeleton:hover {
  border-color: #3d3d3d !important;
  transform: none !important;
  box-shadow: none !important;
}

/* Shimmer animation for skeleton loading */
.shimmer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.05),
    transparent
  );
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}